/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import dayjs from '@/lib/dayjs';

/**
 * Determines if "Free Expedited" should be shown instead of "Free Next Day"
 * Based on PDB-3288 requirements:
 * - Friday after NDD cutoff through end of cutoff Sunday
 * - The cutoff time varies by FedEx ramp/shipping zip code
 *
 * @param cutOffDate - The fulfillment cutoff date/time string
 * @param testTimeOverride - Optional test time override from feature flag for testing
 * @returns true if "Expedited" should be shown, false if "Next Day" should be shown
 */
export const shouldShowExpedited = (cutOffDate?: string, testTimeOverride?: string): boolean => {
  if (!cutOffDate) {
    return false;
  }

  // Development/Testing override - allows simulation of different times for testing
  // Use feature flag TEST_TIME_OVERRIDE to override current time
  // Example values: "2025-01-18T10:00:00Z" (Saturday 10am), "2025-01-19T15:00:00Z" (Sunday 3pm)
  const now = testTimeOverride ? dayjs(testTimeOverride) : dayjs();

  const cutOff = dayjs(cutOffDate);
  const currentDayOfWeek = now.day(); // 0 = Sunday, 5 = Friday, 6 = Saturday

  // Saturday (any time)
  if (currentDayOfWeek === 6) {
    return true;
  }

  // Sunday (any time)
  if (currentDayOfWeek === 0) {
    return true;
  }

  // Friday after cutoff time
  if (currentDayOfWeek === 5 && now.isAfter(cutOff)) {
    return true;
  }

  return false;
};
